import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useHeatmapConfigQuery,
  useUpdateHeatmapConfigMutation,
} from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import svgIcons from "../../../assets/svg/svgIcons";
import Accordian from "../../../components/accordian";
import FormActionButtons from "../../../components/admin/formActionButtons";
import HeaderView from "../../../components/admin/headerView";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import { APP_CONFIG } from "../../../constants/constants";
import { HeatmapAdminAuthorizedGroupsForm } from "./heatmap-admin-authorized-groups";
import { HeatmapAdminGeneralDetailForm } from "./heatmap-admin-general";
import {
  heatmapAdminInitialValues,
  heatmapAdminYupValidationSchema,
} from "./validation";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";

const ScreenAdminHeatmap: React.FC = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.heatmap",
  });

  const { getUserACLAccessLevel } = useACL();
  const userHasWriteAccess =
    getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_HEATMAP_CONFIG_ADMIN) >=
    ROLE_ACCESS_LEVELS.WRITE;

  // flag to enable form editing
  const [userCanEdit, setUserCanEdit] = useState<boolean>(userHasWriteAccess);

  const { data: dataHeatmapConfig, isFetching: isFetchingHeatmapConfig } =
    useHeatmapConfigQuery();

  const [
    updateHeatmapConfigAPITrigger,
    { isLoading: isLoadingUpdateHeatmapConfig },
  ] = useUpdateHeatmapConfigMutation();

  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(!isFetchingHeatmapConfig);
  }, [isFetchingHeatmapConfig]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: heatmapAdminInitialValues,
    validationSchema: heatmapAdminYupValidationSchema,
    onSubmit: (values) => {
      // call api for updating user
      updateHeatmapConfigAPITrigger(values)
        .unwrap()
        .then((_) => {
          toast.success(t("toast.config_updated"));
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (
      !!dataHeatmapConfig &&
      !!dataHeatmapConfig?.results?.[0] &&
      !isFetchingHeatmapConfig
    ) {
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        ...heatmapAdminInitialValues,
        ...dataHeatmapConfig?.results?.[0],
        ...(dataHeatmapConfig?.results?.[0]?.legends.length === 0
          ? {
              legends: heatmapAdminInitialValues.legends,
            }
          : {}),
      });
      setUserCanEdit(false);
      setTimeout(() => {
        setFormikValuesReady(true);
        // disabled 7/10/2025 to keep fields disabled
        // lets make the user click on edit button to enable the fields for editing
        // setUserCanEdit(false);
      }, 200); // simulate render delay for select pre-selected values
    }
    // validate form and show errors if any
    if (!!dataHeatmapConfig && !isFetchingHeatmapConfig) {
      setTimeout(() => {
        validateForm();
      }, 200);
    }
  }, [dataHeatmapConfig, isFetchingHeatmapConfig]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
    validateForm,
  } = formik;
  if (APP_CONFIG.DEBUG.ALL) {
    console.log("ERR", errors);
    console.log("VAL", values);
  }

  // handle edit user
  const handleUpdateConfig = () => {
    if (userCanEdit) {
      console.log("[handleUpdateConfig]");
      formik.handleSubmit();
    }
  };

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleUpdateConfig
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isFetchingHeatmapConfig ||
                isLoadingUpdateHeatmapConfig ||
                (userCanEdit && !!Object.keys(errors).length) ||
                !userHasWriteAccess,
            },
          ]}
        />
      </>
    );
  }, [
    isFetchingHeatmapConfig,
    isLoadingUpdateHeatmapConfig,
    userCanEdit,
    userHasWriteAccess,
    errors,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleUpdateConfig(),
              disabled:
                isFetchingHeatmapConfig ||
                isLoadingUpdateHeatmapConfig ||
                !!Object.keys(errors).length ||
                !userHasWriteAccess,
            },
          ]}
        />
      </>
    );
  }, [
    isFetchingHeatmapConfig,
    isLoadingUpdateHeatmapConfig,
    userCanEdit,
    userHasWriteAccess,
    errors,
  ]);

  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2 pt-4`}>
        <div className="flex items-center gap-4">
          {dataHeatmapConfig?.results?.length === 0 ? (
            <>
              <p className="font-semibold text-accent-blue-dark text-base leading-6">
                {tMain("admins.completeCreation")}
              </p>
              <div className="flex-grow"></div>
              {isFetchingHeatmapConfig && (
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="loading-animation"
                  className="w-8 h-8"
                />
              )}
              {userCanEdit && !!Object.keys(errors).length && (
                <FillAllRequiredFieldsCaption />
              )}
              <div className="w-24">{MemoizedFormActionSaveButton}</div>
            </>
          ) : (
            <>
              <div className="flex-grow"></div>
              {isFetchingHeatmapConfig && (
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="loading-animation"
                  className="w-8 h-8"
                />
              )}
              {userCanEdit && !!Object.keys(errors).length && (
                <FillAllRequiredFieldsCaption />
              )}
              {MemoizedFormActionButtons}
            </>
          )}
        </div>
        <div className="py-2 gap-4">
          <div
            className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingHeatmapConfig ? "opacity-40" : ""}`}
          >
            <form onSubmit={handleSubmit}>
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                <pre className="col-span-12 text-[8px] absolute right-0 bg-white/80 z-10">
                  {JSON.stringify(values, null, 2)}
                </pre>
              )}
              <Accordian title={t("accord_details")} openByDefault={true}>
                <HeatmapAdminGeneralDetailForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  formikSetValue={formik.setFieldValue}
                  handleBlur={handleBlur}
                  formikSetTouched={formik.setFieldTouched}
                  userCanEdit={userCanEdit}
                  loadingData={isFetchingHeatmapConfig || !formikValuesReady}
                />
              </Accordian>
              <Accordian
                title={t("accord_authorized_groups")}
                openByDefault={true}
              >
                <HeatmapAdminAuthorizedGroupsForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  formikSetValue={formik.setFieldValue}
                  handleBlur={handleBlur}
                  formikSetTouched={formik.setFieldTouched}
                  userCanEdit={userCanEdit}
                  loadingData={isFetchingHeatmapConfig || !formikValuesReady}
                />
              </Accordian>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScreenAdminHeatmap;
